import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { MerchantAttributesType, PaymentModeType } from '../types/merchantAttributes'
import { parseMerchantData, parsePaymentModes } from '../utils/parsers'
import { apiURI, constants } from '../utils/constants'
import useSWR from 'swr'
import { fetcher, getRequest, staticOptions } from '../core/api-client'
import { hasAuthCookie } from '../utils/helpers'
import { useAuth } from './AuthContext'

enum MerchantActionType {
  SET_MERCHANT_DATA = 'SET_MERCHANT_DATA',
  SET_PAYMENT_MODES = 'SET_PAYMENT_MODES'
}

type MerchantAction =
  | { type: MerchantActionType.SET_MERCHANT_DATA; payload: { data: any; parse: boolean } }
  | { type: MerchantActionType.SET_PAYMENT_MODES; payload: { data: any } }

interface MerchantTypeContext {
  state: {
    merchant: MerchantAttributesType | null
    paymentModes: PaymentModeType[]
  }
  actions: {
    setMerchantData: (data: any, parse?: boolean) => void
    setPaymentModes: (data: any) => void
    mutatePaymentModes: () => void
    mutateMerchantAttributes: () => void
  }
  getters: {
    merchant: MerchantAttributesType | null
    paymentModes: PaymentModeType[]
  }
}

interface MerchantState {
  merchant: MerchantAttributesType | null
  paymentModes: PaymentModeType[]
}

const initialState: MerchantState = {
  merchant: null,
  paymentModes: []
}

function merchantReducer(state: MerchantState = initialState, action: MerchantAction): MerchantState {
  switch (action.type) {
    case MerchantActionType.SET_MERCHANT_DATA:
      return { ...state, merchant: action.payload.parse ? parseMerchantData(action.payload.data) : action.payload.data }
    case MerchantActionType.SET_PAYMENT_MODES:
      return { ...state, paymentModes: action.payload.data }
    default:
      return state
  }
}

const MerchantContext = createContext<MerchantTypeContext | undefined>(undefined)

const MerchantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(merchantReducer, initialState)
  const { loggedIn } = useAuth()

  const {
    data: merchantData,
    isValidating,
    mutate: mutateMerchantAttributes
  } = useSWR<any, any, [string, string | undefined] | null>(
    hasAuthCookie() ? ['/merchant/attributes', apiURI.FLO_KRATOS_CHECKOUT] : null,
    ([url, baseURI]) => fetcher(url, baseURI),
    staticOptions
  )

  const { data: paymentResponse, mutate: mutatePaymentModes } = useSWR(
    `/merchant/payment-modes`,
    async url => {
      if (!loggedIn) {
        return null
      }

      const response: any = await getRequest(url, apiURI.FMS_ENGINE)
      return response
    },
    staticOptions
  )

  useEffect(() => {
    if (isValidating || !merchantData) return
    setMerchantData(merchantData)
  }, [isValidating, merchantData])

  useEffect(() => {
    if (paymentResponse) {
      setPaymentModes(paymentResponse)
    }
  }, [paymentResponse])

  const setMerchantData = (data: any, parse = true) => {
    dispatch({
      type: MerchantActionType.SET_MERCHANT_DATA,
      payload: { data, parse }
    })
  }

  const setPaymentModes = (data: any) => {
    if (!Boolean(data)) return
    const parsedPaymentModes = parsePaymentModes(data)

    dispatch({
      type: MerchantActionType.SET_PAYMENT_MODES,
      payload: { data: parsedPaymentModes }
    })
  }

  const actions = {
    setMerchantData,
    setPaymentModes,
    mutatePaymentModes,
    mutateMerchantAttributes
  }

  const getters = {
    merchant: state.merchant,
    paymentModes: state.paymentModes
  }

  return <MerchantContext.Provider value={{ state, actions, getters }}>{children}</MerchantContext.Provider>
}

const useMerchant = () => {
  const context = useContext(MerchantContext)
  if (!context) {
    throw new Error('useMerchant must be used within a MerchantProvider')
  }
  return context
}

export { MerchantProvider, useMerchant }
